import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, User, Mail, Phone, Building2, MessageSquare, Camera, CheckCircle2, Loader2, AlertCircle, X, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useSeo } from '../hooks/useSeo';

const TURNSTILE_SITE_KEY = '0x4AAAAAADq6rZuJ4jQcm8ev';

const SERVICE_OPTIONS = [
  'Kamery zewnętrzne', 'Kamery wewnętrzne', 'Alarm',
  'Domofon', 'Anty-reklama Wi-Fi', 'Sieć IT', 'Szafka rack', 'Opieka techniczna',
];

type FormStatus = 'idle' | 'sending' | 'sent' | 'error';

export function WycenaPage() {
  useSeo({
    title: 'Bezpłatna wycena monitoringu i kamer Mielec (39-300) | kamerymielec.pl',
    description:
      'Zamów bezpłatną wycenę montażu kamer, monitoringu CCTV, alarmów, anty-reklamy Wi-Fi i sieci IT w Mielcu oraz w promieniu do 250 km (Rzeszów, Tarnów, Dębica, Stalowa Wola, Tarnobrzeg, Sandomierz). Indywidualna oferta, odpowiedź w 24h, bez zobowiązań.',
    canonicalPath: '/wycena',
    keywords:
      'wycena monitoringu Mielec, darmowa wycena kamer Mielec, koszt montażu kamer Mielec, oferta CCTV Mielec, wycena alarmu Mielec, monitoring cennik mielec',
    breadcrumbs: [
      { name: 'Strona główna', path: '/' },
      { name: 'Bezpłatna wycena', path: '/wycena' },
    ],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Bezpłatna wycena monitoringu Mielec',
      url: 'https://kamerymielec.pl/wycena',
      inLanguage: 'pl-PL',
      isPartOf: { '@type': 'WebSite', name: 'Kamery Mielec - ONEXO', url: 'https://kamerymielec.pl/' },
      potentialAction: {
        '@type': 'CommunicateAction',
        name: 'Zamów bezpłatną wycenę',
        target: 'https://kamerymielec.pl/wycena',
      },
    },
  });

  const formRef = useRef<HTMLFormElement>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const captchaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaError, setCaptchaError] = useState<string | null>(null);

  const toggleService = (service: string) => {
    setSelectedServices((prev) =>
      prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service],
    );
  };

  useEffect(() => {
    return () => {
      if (captchaTimeoutRef.current) clearTimeout(captchaTimeoutRef.current);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    const honeypot = formRef.current.querySelector<HTMLInputElement>('[name="website_url"]');
    if (honeypot?.value) return;

    if (!TURNSTILE_SITE_KEY) {
      setCaptchaError('Brak klucza CAPTCHA. Skontaktuj się z nami bezpośrednio.');
      setShowCaptcha(true);
      return;
    }

    setCaptchaError(null);
    setShowCaptcha(true);

    captchaTimeoutRef.current = setTimeout(() => {
      setCaptchaError(
        'Weryfikacja nie załadowała się. Wyłącz blokadę reklam (AdBlock) lub spróbuj w innej przeglądarce.'
      );
    }, 15000);
  };

  const onTurnstileSuccess = useCallback(async (token: string) => {
    if (captchaTimeoutRef.current) clearTimeout(captchaTimeoutRef.current);
    setShowCaptcha(false);
    setCaptchaError(null);
    setStatus('sending');

    if (!formRef.current) return;

    const fd = new FormData(formRef.current);
    const payload = {
      name: String(fd.get('from_name') || ''),
      company: String(fd.get('from_company') || ''),
      email: String(fd.get('from_email') || ''),
      phone: String(fd.get('from_phone') || ''),
      services: String(fd.get('services') || ''),
      message: String(fd.get('message') || ''),
      website_url: String(fd.get('website_url') || ''),
      token,
    };

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus('sent');
    } catch (err) {
      console.error('Contact send error:', err);
      setStatus('error');
      turnstileRef.current?.reset();
    }
  }, []);

  const onTurnstileError = useCallback(() => {
    if (captchaTimeoutRef.current) clearTimeout(captchaTimeoutRef.current);
    setCaptchaError(
      'Weryfikacja nie powiodła się. Wyłącz blokadę reklam (AdBlock) lub spróbuj ponownie.'
    );
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-20 px-5 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[min(600px,120vw)] h-[min(600px,120vw)] bg-primary/[0.04] dark:bg-primary/[0.06] rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[min(400px,80vw)] h-[min(400px,80vw)] bg-cyan-500/[0.03] rounded-full blur-[100px]" />
      </div>

      <div className="relative max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs md:text-sm font-semibold mb-4">
            <Camera size={14} /> Bezpłatna wycena monitoringu - Mielec i promień do 250 km
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
            Otrzymaj bezpłatną <span className="gradient-text">wycenę</span>
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Opisz, co chcesz zabezpieczyć: dom, firmę czy posesję w Mielcu lub okolicach (do 250 km), a&nbsp;my przygotujemy indywidualną ofertę dopasowaną do Twojego budżetu. Bez zobowiązań, odpowiedź w&nbsp;24h.
          </p>
        </motion.div>

        {status === 'sent' ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto text-center py-16"
          >
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} className="text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Dziękujemy!</h2>
            <p className="text-muted-foreground mb-6">
              Twoje zapytanie zostało wysłane. Odezwiemy się najszybciej jak to możliwe, zwykle w ciągu 24 godzin.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary text-white font-semibold hover:brightness-110 transition-all"
            >
              Wróć na stronę główną
            </Link>
          </motion.div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
            <motion.form
              ref={formRef}
              onSubmit={handleSubmit}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-3 space-y-5"
            >
              <div className="absolute overflow-hidden" style={{ left: '-9999px' }} aria-hidden="true">
                <input type="text" name="website_url" tabIndex={-1} autoComplete="off" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Imię i nazwisko</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      name="from_name"
                      required
                      placeholder="Jan Kowalski"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-card border border-border/60 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Firma (opcjonalnie)</label>
                  <div className="relative">
                    <Building2 size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      name="from_company"
                      placeholder="Nazwa firmy"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-card border border-border/60 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    />
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1.5 block">E-mail</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      name="from_email"
                      required
                      placeholder="jan@firma.pl"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-card border border-border/60 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Telefon</label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="tel"
                      name="from_phone"
                      required
                      placeholder="+48 000 000 000"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-card border border-border/60 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Czego potrzebujesz?</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {SERVICE_OPTIONS.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleService(tag)}
                      className={`inline-block px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        selectedServices.includes(tag)
                          ? 'bg-primary text-white border-primary'
                          : 'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                <input type="hidden" name="services" value={selectedServices.join(', ') || 'Nie wybrano'} />
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Opis potrzeb</label>
                <div className="relative">
                  <MessageSquare size={16} className="absolute left-3.5 top-3.5 text-muted-foreground" />
                  <textarea
                    rows={5}
                    name="message"
                    required
                    placeholder="Opisz co chcesz zabezpieczyć, ile kamer potrzebujesz, jaka jest lokalizacja..."
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-card border border-border/60 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
                  />
                </div>
              </div>

              {status === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm"
                >
                  <AlertCircle size={16} />
                  Wystąpił błąd. Spróbuj ponownie lub zadzwoń do nas.
                </motion.div>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-primary text-white font-bold text-base hover:brightness-110 transition-all shadow-lg shadow-primary/20 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'sending' ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Wysyłanie...
                  </>
                ) : (
                  <>
                    <Send size={18} /> Wyślij zapytanie
                  </>
                )}
              </button>

              <p className="text-xs text-muted-foreground text-center">
                Wysyłając formularz, zgadzasz się z naszą{' '}
                <Link to="/polityka-prywatnosci" className="text-primary underline underline-offset-2">polityką prywatności</Link>.
              </p>
            </motion.form>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-2 space-y-5"
            >
              <div className="rounded-2xl bg-card/80 backdrop-blur-sm border border-border/40 p-6 space-y-4">
                <h3 className="font-bold text-lg text-foreground">Jak to działa?</h3>
                {[
                  { step: '1', text: 'Wypełnij formularz z opisem potrzeb' },
                  { step: '2', text: 'Nasz specjalista skontaktuje się w 24h' },
                  { step: '3', text: 'Umówimy wizję lokalną i wycenę' },
                  { step: '4', text: 'Otrzymasz szczegółową ofertę' },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-primary">{item.step}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{item.text}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl bg-gradient-to-br from-primary to-primary-dark p-6 text-white">
                <h3 className="font-bold text-lg mb-2">Wolisz zadzwonić?</h3>
                <p className="text-white/70 text-sm mb-4">Jesteśmy dostępni od pon. do pt. 9:00 do 18:00</p>
                <a
                  href="tel:+48664896662"
                  className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white/20 backdrop-blur-sm font-bold text-sm hover:bg-white/30 transition-all"
                >
                  <Phone size={16} /> +48 664 896 662
                </a>
              </div>
            </motion.div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showCaptcha && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[1300] flex items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => { setShowCaptcha(false); setCaptchaError(null); if (captchaTimeoutRef.current) clearTimeout(captchaTimeoutRef.current); turnstileRef.current?.reset(); }}
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="relative bg-card border border-border/60 rounded-3xl shadow-2xl p-6 sm:p-8 max-w-sm w-full"
            >
              <button
                onClick={() => { setShowCaptcha(false); setCaptchaError(null); if (captchaTimeoutRef.current) clearTimeout(captchaTimeoutRef.current); turnstileRef.current?.reset(); }}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <X size={18} />
              </button>

              <div className="text-center mb-6">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3 ${captchaError ? 'bg-red-500/10' : 'bg-primary/10'}`}>
                  {captchaError ? <AlertCircle size={24} className="text-red-500" /> : <ShieldCheck size={24} className="text-primary" />}
                </div>
                <h3 className="font-bold text-lg text-foreground">
                  {captchaError ? 'Problem z weryfikacją' : 'Potwierdź, że nie jesteś robotem'}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {captchaError || 'Weryfikacja zajmie chwilę'}
                </p>
              </div>

              {!captchaError && (
                <div className="flex justify-center">
                  {TURNSTILE_SITE_KEY && (
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={onTurnstileSuccess}
                      onError={onTurnstileError}
                      onExpire={onTurnstileError}
                      options={{ theme: 'auto', size: 'normal' }}
                    />
                  )}
                </div>
              )}

              {captchaError && (
                <div className="flex flex-col gap-2 mt-2">
                  <button
                    onClick={() => {
                      setCaptchaError(null);
                      turnstileRef.current?.reset();
                      captchaTimeoutRef.current = setTimeout(() => {
                        setCaptchaError('Weryfikacja nie załadowała się. Wyłącz blokadę reklam (AdBlock) lub spróbuj w innej przeglądarce.');
                      }, 15000);
                    }}
                    className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
                  >
                    Spróbuj ponownie
                  </button>
                  <a
                    href="tel:+48664896662"
                    className="w-full py-2.5 rounded-xl border border-border text-center text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                  >
                    Zadzwoń: +48 664 896 662
                  </a>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
